/**
 * MembershipSection — the third line of the identity group on the profile hub.
 *
 * IS 010-member-tier-read: `/v1/members/me` now carries the membership tier +
 * start date (resolved server-side in the member-profile adapter). When both are
 * present this renders the summary line "{tier} {date} alkaen"; when the IS omits
 * or cannot resolve them (`tier`/`startDate` null) it keeps the honest
 * "not available" line (P7 — explicit over fabricated), which was the whole
 * section before 034's live read had the data.
 *
 * DISPLAY-ONLY: tier + date never gate access — membership *status* (valid/lapsed)
 * is Kilta-sourced (028/042) and lives elsewhere. This is subdued display copy.
 *
 * The tier label comes from the IS in Finnish (official YTK membership-category name,
 * treated as locale-invariant), so in the English UI it is wrapped in `lang="fi"` so
 * assistive tech pronounces it as Finnish, not English (WCAG 3.1.2). That is why the
 * summary is built as JSX rather than a single filled string: `{date}` is filled as
 * text (the surrounding frame — "alkaen" / "since" — is UI-locale), and the tier is
 * the one part in a different language.
 *
 * No raw colour literals (FR-013) — token classes only.
 *
 * Owned by feature 018-omat-tiedot; reshaped by 034-member-is-reads US2; membership
 * summary added by member-tier-display (consumes IS 010).
 */

import type { JSX, ReactNode } from 'react';
import { fill } from '../../i18n/fill.js';
import { useLanguage } from '../../i18n/useLanguage.js';
import { useTranslate } from '../../i18n/useTranslate.js';
import { formatIsoDate } from './formatIsoDate.js';

export interface MembershipSectionProps {
  /** Resolved membership tier label, or `null` when the IS omits/can't resolve it. */
  readonly tier: string | null;
  /** Membership start date (ISO `YYYY-MM-DD`), or `null` when the IS carries none. */
  readonly startDate: string | null;
}

export function MembershipSection({ tier, startDate }: MembershipSectionProps): JSX.Element {
  const t = useTranslate();
  const { active: locale } = useLanguage();

  let content: ReactNode;
  // Truthy guard (not `!== null`): a non-conformant source can hand us `undefined` or an
  // empty string — either must degrade to the fallback, never reach `formatIsoDate`
  // (`undefined.split` would throw and crash the whole profile hub into its error state).
  if (tier && startDate) {
    // Fill ONLY `{date}` (as text); leave `{tier}` as a verbatim placeholder (fill()
    // leaves unknown tokens untouched) so we can split on it and wrap the Finnish
    // tier label in `lang="fi"` (WCAG 3.1.2). `after` defaults to '' defensively if a
    // (malformed) template ever lacks the `{tier}` token.
    const filled = fill(t('profile.membershipSummary'), { date: formatIsoDate(startDate, locale) });
    const [before, after = ''] = filled.split('{tier}');
    content = (
      <>
        {before}
        <span lang="fi">{tier}</span>
        {after}
      </>
    );
  } else {
    content = t('profile.membershipUnavailable');
  }

  // One plain muted line, centered to match the identity block above — no border,
  // no label, no badge (matching the proto's subdued membership note).
  return <p className="text-center text-sm text-muted-foreground">{content}</p>;
}
